import { h, defineComponent, SetupContext, VNodeChildren } from 'vue';
// @ts-ignore
import { BCheckbox } from 'buefy/dist/esm/checkbox';
import { ValidationError } from '@/entities/validation';
import { SetterCallback } from '@/ui/types';
import { Field, FieldProps } from './Field';
import { StarredAdItem } from '@/ui/components/upgrade/StarredAdItem';
import { Tooltip } from '@/ui/components/Tooltip';
import { featureExplanationIcon } from '@/ui/components/icon/icons';

export interface CheckboxFieldProps {
  name: string;
  label: string;
  value: boolean;
  required: boolean;
  disabled: boolean;
  errors: ValidationError[];
  hideLabel?: boolean;
  horizontal?: boolean;
  starred?: boolean;
  nativeValue?: string | number;
  tooltipText?: string;
}

export const CheckboxField = defineComponent<CheckboxFieldProps>({
  name: 'CheckboxField',
  props: [
    'name', 'label', 'value', 'required', 'errors', 'disabled',
    'hideLabel', 'horizontal', 'starred', 'nativeValue', 'tooltipText',
  ],
  setup(props, context: SetupContext) {
    const newFeatureWrapper = () => context.slots.newFeature?.();

    const tooltipWrap = (content: VNodeChildren) => props.tooltipText
      ? [content, h(Tooltip, {
        props: {
          label: props.tooltipText,
          position: 'is-bottom',
          multilined: true,
          classes: 'ml-2',
        },
      }, [
        featureExplanationIcon(),
      ])]
      : [content];

    const labelWrap = (content: VNodeChildren) => [props.starred
      ? h(StarredAdItem, { props: { isStarred: props.starred } }, tooltipWrap(content))
      : h('span', tooltipWrap(content))];

    return function render() {
      const fieldProps: FieldProps = {
        label: props.label,
        name: props.name,
        required: props.required,
        errors: props.errors ?? [],
        hideLabel: props.hideLabel ?? true,
        horizontal: props.horizontal,
      };

      return h(Field, { props: fieldProps }, [
        h(BCheckbox, {
          props: { value: props.value, nativeValue: props.nativeValue },
          attrs: {
            ...context.attrs,
            name: props.name,
            disabled: props.disabled,
          },
          on: {
            input: (newValue: boolean) => {
              context.emit('input', newValue);
            },
          },
        }, labelWrap(props.label)),
        newFeatureWrapper(),
      ]);
    };
  },
});

export function checkboxField(
  props: CheckboxFieldProps,
  input: SetterCallback<boolean>,
  fieldClass?: string | null,
  content?: VNodeChildren,
) {
  return h(CheckboxField, {
    class: fieldClass ?? 'form-checkbox',
    props,
    on: { input },
  }, content);
}
